import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import UIkit from "uikit";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";

import RecordsPerPage from "../../../common/RecordsPerPage";
import OpsSubscriptionRequired, {
  hasOpsSubscription,
} from "../../../common/OpsSubscriptionRequired";
import Pagination from "../../../common/Pagination";
import QRPart from "../../../common/Qrcode";
import Tags from "../../../common/Tags";
import Dialog from "../../../common/Dialog";
import StorageItemAdd from "./add";
import { AppContexts } from "../../../../providers";
import StorageService from "../../../../services/storage.service";
import {
  DEFAULT_RPP,
  INITIAL_ITEMS,
  ITEM_CATEGORY_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TRACKING_MODE_OPTIONS,
  UNIT_OPTIONS,
  labelFor,
} from "../data";

export default function StorageItemsIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const { user: account } = useContext(AppContexts.AuthContext);
  const settingsContext = useContext(AppContexts.SettingsContext);
  const opsSubscriptionLocked = !hasOpsSubscription(account);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [items, setItems] = useState(INITIAL_ITEMS);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [criteria, setCriteria] = useState({
    search: "",
    category: "",
    tracking_mode: "",
    status: "",
    page: 0,
  });
  const getSettings = settingsContext.get;

  const loadItems = useCallback(async () => {
    try {
      const resp = await StorageService.listItems({ per: 1000 });
      setItems(resp.data?.paginated || []);
    } catch (err) {
      UIkit.notification("Failed to load storage items.", "error");
    }
  }, []);

  useEffect(() => {
    document.title = "Storage Items - RueBaRue";
    getSettings(["tags"]);
    loadItems();
  }, [getSettings, loadItems]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page: ["search", "category", "tracking_mode", "status"].includes(key)
        ? 0
        : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const toggleActive = useCallback((itemId) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, active: !item.active } : item,
      ),
    );
  }, []);

  const handleSelectedTags = useCallback((tags, item) => {
    setItems((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, tags } : row)),
    );
  }, []);

  const openAdd = useCallback(() => {
    setEditingItem(null);
    UIkit.offcanvas("#modal-storage-item-add").show();
  }, []);

  const openEdit = useCallback((item) => {
    setEditingItem(item);
    UIkit.offcanvas("#modal-storage-item-add").show();
  }, []);

  const handleSaveItem = useCallback(async (payload) => {
    setIsProcessing(true);
    try {
      const resp = payload.id
        ? await StorageService.updateItem(payload.id, payload)
        : await StorageService.createItem(payload);
      const saved = resp.data;

      setItems((prev) => {
        if (payload.id) {
          return prev.map((item) => (item.id === payload.id ? saved : item));
        }
        return [saved, ...prev];
      });

      UIkit.notification("Item saved", "success");
      return true;
    } catch (err) {
      UIkit.notification("Failed to save item.", "error");
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const openDelete = useCallback((item) => {
    setDeletingItem(item);
  }, []);

  const handleDeleteItem = useCallback(async () => {
    if (!deletingItem) return;

    setIsProcessing(true);
    try {
      await StorageService.deleteItem(deletingItem.id);
      setItems((prev) => prev.filter((row) => row.id !== deletingItem.id));
      UIkit.modal("#modal-storage-item-delete").hide();
      setDeletingItem(null);
      UIkit.notification("Item deleted", "success");
    } catch (err) {
      UIkit.notification("Failed to delete item.", "error");
    } finally {
      setIsProcessing(false);
    }
  }, [deletingItem]);

  const cancelDelete = useCallback(() => {
    setDeletingItem(null);
  }, []);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (criteria.category && item.category !== criteria.category) return false;
      if (criteria.tracking_mode && item.tracking_mode !== criteria.tracking_mode) {
        return false;
      }
      if (criteria.status === "active" && !item.active) return false;
      if (criteria.status === "inactive" && item.active) return false;

      if (criteria.search) {
        const query = criteria.search.toLowerCase();
        const tagNames = (item.tags || []).map((tag) => tag.name).join(" ");
        const haystack = `${item.name || ""} ${item.sku || ""} ${
          item.preferred_supplier || ""
        } ${tagNames}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });
  }, [items, criteria]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / recordsPerPage));
  const pageItems = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage,
  );

  return (
    <>
      <section>
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Storage Items</h1>
        </div>

        {opsSubscriptionLocked && <OpsSubscriptionRequired />}

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <button
              className="uk-button hms-btn hms-red-btn"
              type="button"
              onClick={openAdd}
              disabled={opsSubscriptionLocked}
              title={opsSubscriptionLocked ? "Ops Subscription Required" : undefined}
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Item
            </button>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <div className="uk-inline uk-margin-small-right">
                    <div className="uk-search uk-search-default uk-form-custom">
                      {criteria.search ? (
                        <button
                          type="button"
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() => handleCriteriaChange("search", "")}
                          aria-label="Clear search"
                        ></button>
                      ) : (
                        <span className="uk-search-icon-flip" uk-search-icon="true" />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Search item, SKU, supplier"
                        value={criteria.search}
                        onChange={(event) => handleCriteriaChange("search", event.target.value)}
                      />
                    </div>
                  </div>

                  {[
                    ["category", ITEM_CATEGORY_OPTIONS, "All Categories"],
                    [
                      "tracking_mode",
                      [{ label: "All Tracking", value: "" }, ...TRACKING_MODE_OPTIONS],
                      "All Tracking",
                    ],
                    ["status", STATUS_FILTER_OPTIONS, "All Statuses"],
                  ].map(([key, options, fallback]) => (
                    <div
                      key={key}
                      className="uk-inline uk-margin-small-right"
                      uk-form-custom="target: > * > span:first-child"
                    >
                      <select
                        value={criteria[key]}
                        onChange={(event) => handleCriteriaChange(key, event.target.value)}
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                        <span className="select-value">
                          {criteria[key] ? labelFor(options, criteria[key]) : fallback}
                        </span>
                        <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                      </button>
                    </div>
                  ))}
                </form>
              </Flex>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th className="uk-table-expand">Item</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th>Tracking</th>
                    <th>Default Reorder</th>
                    <th>Default Par</th>
                    <th>Supplier</th>
                    <th>Tags</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan={11}>
                        <div className="uk-text-center uk-padding-small uk-text-meta">
                          No items found. Adjust your filters.
                        </div>
                      </td>
                    </tr>
                  )}

                  {pageItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="autosend-checkbox">
                          <label className="setting-switch">
                            <input
                              type="checkbox"
                              checked={!!item.active}
                              onChange={() => toggleActive(item.id)}
                            />
                            <span className="setting-slider setting-round"></span>
                          </label>
                        </div>
                      </td>
                      <td className="semi-bold-font">
                        {item.name}
                      </td>
                      <td>{item.sku || "-"}</td>
                      <td>{labelFor(ITEM_CATEGORY_OPTIONS, item.category)}</td>
                      <td>
                        {labelFor(UNIT_OPTIONS, item.unit)}
                        {item.pack_size > 1 ? ` x ${item.pack_size}` : ""}
                      </td>
                      <td>{labelFor(TRACKING_MODE_OPTIONS, item.tracking_mode)}</td>
                      <td>{item.default_reorder_point ?? "-"}</td>
                      <td>{item.default_par_level ?? "-"}</td>
                      <td>{item.preferred_supplier || "-"}</td>
                      <td className="hms-add-tag">
                        <Tags
                          value={item.tags || []}
                          onChange={(tags) => handleSelectedTags(tags, item)}
                        />
                      </td>
                      <td className="hms-action-btn">
                        <div className="uk-inline">
                          <button
                            className="uk-button"
                            type="button"
                            onClick={() => openEdit(item)}
                            uk-tooltip="Edit"
                          >
                            <img src="/images/edit.svg" alt="Edit" />
                          </button>
                          <QRPart
                            idValue={`storage-item-${item.id}`}
                            qrUrl={`rbr:storage_item:${item.id}`}
                            iconSrc="/images/qr-code.svg"
                            tooltip="Download QR Code"
                          />
                          <button
                            className="uk-button"
                            type="button"
                            uk-toggle="target: #modal-storage-item-delete"
                            onClick={() => openDelete(item)}
                            disabled={isProcessing}
                            uk-tooltip="Delete"
                          >
                            <img src="/images/delete.svg" alt="Delete" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Flex alignment="between top" className="guest-form-top-filters">
                <Pagination
                  count={totalPages}
                  index={Math.min(criteria.page, totalPages - 1)}
                  onIndexChanged={handleIndexChanged}
                />
                <RecordsPerPage />
              </Flex>
            </div>
          </div>
        </div>
      </section>

      <StorageItemAdd
        item={editingItem}
        flagIsNew={!editingItem}
        flagIsProcessing={isProcessing}
        onSave={handleSaveItem}
        onCancel={() => setEditingItem(null)}
      />

      <Dialog id="modal-storage-item-delete">
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
            onClick={cancelDelete}
          ></button>
          <h2 className="uk-modal-title">Delete Item</h2>
          <p>
            Are you sure you want to delete <b>{deletingItem?.name}</b>?
          </p>
          <p className="uk-text-right">
            <button
              className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel"
              type="button"
              onClick={cancelDelete}
            >
              Cancel
            </button>
            <button
              className="uk-button hms-btn hms-gray-btn"
              type="button"
              onClick={handleDeleteItem}
              disabled={isProcessing}
            >
              Delete
            </button>
          </p>
        </div>
      </Dialog>
    </>
  );
}
